/**
 * RestaurantList - Smart Container Component
 * Orchestriert Cards, States und Infinite Scroll
 */

import React, { memo, useRef, useEffect } from 'react';
import { RestaurantCard } from './RestaurantCard';
import { 
  LoadingState, 
  EmptyState, 
  ErrorState, 
  RefreshingIndicator,
  LoadMoreIndicator 
} from './SearchStates';
import type { RestaurantWithDetails, ViewMode } from '@/types/search';

// ============================================
// TYPES
// ============================================

interface RestaurantListProps {
  restaurants: RestaurantWithDetails[];
  viewMode: ViewMode;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
  canLoadMore: boolean;
  onRetry: () => void;
  onResetFilters: () => void;
  onPageChange: (page: number) => void;
  onChangeLocation?: () => void;
}

// ============================================
// MAIN COMPONENT
// ============================================

export const RestaurantList = memo(function RestaurantList({
  restaurants,
  viewMode,
  isLoading,
  isFetching,
  isError,
  itemsPerPage,
  totalPages,
  currentPage,
  canLoadMore,
  onRetry,
  onResetFilters,
  onPageChange,
  onChangeLocation
}: RestaurantListProps) {
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const useLoadMore = viewMode === 'list';
  
  // Infinite scroll observer (list view only)
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !canLoadMore || !useLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && canLoadMore) {
          onPageChange(Math.min(currentPage + 1, totalPages));
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(loadMoreTriggerRef.current);
    return () => observer.disconnect();
  }, [canLoadMore, totalPages, currentPage, onPageChange, useLoadMore]);
  
  // ============================================
  // ERROR STATE
  // ============================================
  
  if (isError) {
    return (
      <ErrorState
        title="Fehler beim Laden"
        description="Die Restaurants konnten nicht geladen werden. Bitte versuche es erneut."
        onRetry={onRetry}
      />
    );
  }
  
  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <LoadingState viewMode={viewMode} itemCount={itemsPerPage} />;
  }
  
  // ============================================
  // EMPTY STATE
  // ============================================
  
  if (restaurants.length === 0) {
    return (
      <EmptyState
        title="Keine Restaurants gefunden"
        description="Mit den aktuellen Filtern haben wir nichts Passendes gefunden."
        suggestions={[
          'Filter zurücksetzen',
          'Größeren Umkreis wählen',
          'Anderen Standort eingeben'
        ]}
        onResetFilters={onResetFilters}
        onChangeLocation={onChangeLocation}
      />
    );
  }
  
  // ============================================
  // RESULTS LIST - Premium Layout
  // ============================================
  
  return (
    <div className="space-y-4">
      {/* SWR Indicator (background refetch) */}
      {!isLoading && isFetching && (
        <div className="mb-4">
          <RefreshingIndicator />
        </div>
      )}
      
      {/* Premium Layout: Featured + Grid */}
      {viewMode === 'list' ? (
        // List View: Clean vertical list with subtle dividers
        <div className="flex flex-col gap-2">
          {restaurants.map((restaurant) => (
            <RestaurantCard
              key={restaurant.id}
              variant="list"
              restaurant={restaurant}
              openingHours={restaurant.openingHours}
              deliveryHours={restaurant.deliveryHours}
              images={restaurant.images}
              cuisines={restaurant.categories}
              distance={(restaurant as any).distanceKm ?? restaurant.distance}
            />
          ))}
        </div>
      ) : (
        // Grid View: Featured first + responsive grid
        <div className="space-y-4">
          {/* Featured Card - First restaurant */}
          {restaurants.length > 0 && (
            <RestaurantCard
              key={restaurants[0].id}
              variant="featured"
              restaurant={restaurants[0]}
              openingHours={restaurants[0].openingHours}
              deliveryHours={restaurants[0].deliveryHours}
              images={restaurants[0].images}
              cuisines={restaurants[0].categories}
              distance={(restaurants[0] as any).distanceKm ?? restaurants[0].distance}
            />
          )}
          
          {/* Responsive Grid - Balanced gaps */}
          {restaurants.length > 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-5">
              {restaurants.slice(1).map((restaurant) => (
                <RestaurantCard
                  key={restaurant.id}
                  variant="grid"
                  restaurant={restaurant}
                  openingHours={restaurant.openingHours}
                  deliveryHours={restaurant.deliveryHours}
                  images={restaurant.images}
                  cuisines={restaurant.categories}
                  distance={(restaurant as any).distanceKm ?? restaurant.distance}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Load More / Pagination */}
      {totalPages > 1 && (
        <div className="mt-6">
          {useLoadMore && (
            <>
              {/* Invisible trigger for infinite scroll */}
              {canLoadMore && <div ref={loadMoreTriggerRef} className="h-px" />}
              <LoadMoreIndicator 
                isLoading={canLoadMore}
                totalCount={restaurants.length}
                hasMore={canLoadMore}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default RestaurantList;
